import { useState, useEffect } from "react";
import {
  fetchEducationList,
  fetchEducationDetailList,
  fetchMyApply,
  createApply,
  deleteApply
} from "../api/educationApi";
import { getCodeList } from "../api/codeApi";
import Toast from "../components/Toast";

const Badge = ({ children, color }) => {
  const colors = {
    blue  : { bg: "#e8f4fd", text: "#1677ff", border: "#bae0ff" },
    green : { bg: "#f6ffed", text: "#52c41a", border: "#b7eb8f" },
    gray  : { bg: "#f5f5f5", text: "#888",    border: "#d9d9d9" },
  };
  const c = colors[color] || colors.blue;
  return (
    <span style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}`, borderRadius: 6, padding: "2px 10px", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
};

// ===================== 상세 페이지 =====================
const DetailPage = ({ item, onBack }) => {
  const [detailList, setDetailList] = useState([]);
  const [myApply,    setMyApply]    = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [toast,      setToast]      = useState("");

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 2500); };

  const loadData = async () => {
    setLoading(true);
    try {
      const [detailRes, myApplyRes] = await Promise.all([
        fetchEducationDetailList(item.edu_year, item.seq_no),
        fetchMyApply(item.edu_year, item.seq_no)
      ]);
      if (detailRes.success)  setDetailList(detailRes.data);
      if (myApplyRes.success) setMyApply(myApplyRes.data.map(a => a.edu_dt));
    } catch (err) {
      showToast("❌ 데이터 조회 실패");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [item]);

  const handleApply = async (edu_dt) => {
    const res = await createApply(item.edu_year, item.seq_no, edu_dt);
    if (res.success) { showToast("✅ 신청이 완료되었습니다."); loadData(); }
    else showToast(`❌ ${res.message}`);
  };

  const handleCancel = async (edu_dt) => {
    const res = await deleteApply(item.edu_year, item.seq_no, edu_dt);
    if (res.success) { showToast("✅ 신청이 취소되었습니다."); loadData(); }
    else showToast(`❌ ${res.message}`);
  };

  return (
    <div style={styles.wrap}>
      <Toast msg={toast} />
      <button style={styles.backBtn} onClick={onBack}>← 목록으로</button>

      {/* 교육 기본정보 */}
      <div style={styles.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, gap: 8 }}>
          <h2 style={{ fontSize: 17, fontWeight: 800, color: "#1a1a2e", margin: 0, lineHeight: 1.4 }}>{item.edu_name}</h2>
          <Badge color="blue">{item.edu_year}년 {item.seq_no}차</Badge>
        </div>
        {item.img_data && (
          <div style={{ marginBottom: 14 }}>
            <img src={item.img_data} alt="교육 이미지"
              style={{ width: '100%', maxHeight: 260, borderRadius: 10, objectFit: 'contain' }} />
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>교육기간</span>
            <span style={styles.infoValue}>{item.start_dt} ~ {item.end_dt}</span>
          </div>
          {item.edu_place && (
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>장소</span>
              <span style={styles.infoValue}>{item.edu_place}</span>
            </div>
          )}
          {item.edu_time && (
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>시간</span>
              <span style={styles.infoValue}>{item.edu_time}</span>
            </div>
          )}
        </div>
      </div>

      {/* 세부일정 + 신청 */}
      <div style={{ ...styles.card, marginTop: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: "#1a1a2e", margin: 0 }}>📅 세부일정 / 교육신청</h3>
          <span style={styles.statBadge}>총 {detailList.length}일</span>
        </div>

        {loading ? (
          <div style={styles.empty}>⏳ 불러오는 중...</div>
        ) : detailList.length === 0 ? (
          <div style={styles.empty}>등록된 세부일정이 없습니다.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {detailList.map((d, i) => {
              const isApplied  = myApply.includes(d.edu_dt);
              const hasAny     = myApply.length > 0;
              const isDisabled = !isApplied && hasAny;

              return (
                <div key={i} style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: isApplied ? "#f6ffed" : "#fafafa",
                  border: `1.5px solid ${isApplied ? "#b7eb8f" : "#f0f0f0"}`,
                  borderRadius: 12,
                  padding: "10px 16px",
                  transition: "all 0.15s"
                }}>
                  {/* 왼쪽 - 날짜 + 장소 + 상태 (한 줄) */}
                  <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 16, fontWeight: 700, color: "#1a1a2e" }}>{d.edu_dt}</span>
                    {d.edu_place && (
                      <span style={{ fontSize: 13, color: "#888" }}>📍 {d.edu_place}</span>
                    )}
                    {isApplied && <Badge color="green">✅ 신청완료</Badge>}
                  </div>

                  {/* 오른쪽 - 버튼 */}
                  <div>
                    {isApplied ? (
                      <button
                        style={{ background: "#fff2f0", border: "1.5px solid #ffccc7", color: "#ff4d4f", borderRadius: 8, padding: "6px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
                        onClick={() => handleCancel(d.edu_dt)}
                      >취소</button>
                    ) : (
                      <button
                        style={{
                          background: isDisabled ? "#f0f0f0" : "linear-gradient(135deg, #1677ff, #4096ff)",
                          border: "none",
                          color: isDisabled ? "#bbb" : "#fff",
                          borderRadius: 8,
                          padding: "6px 16px",
                          fontSize: 13,
                          fontWeight: 700,
                          cursor: isDisabled ? "not-allowed" : "pointer",
                          boxShadow: isDisabled ? "none" : "0 2px 8px rgba(22,119,255,0.3)"
                        }}
                        onClick={() => !isDisabled && handleApply(d.edu_dt)}
                        disabled={isDisabled}
                        title={isDisabled ? "이미 다른 날짜에 신청하셨습니다." : ""}
                      >신청</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* 안내 문구 */}
        <p style={{ fontSize: 12, color: "#aaa", marginTop: 16, textAlign: "center" }}>
          ※ 교육은 1개 일정만 신청 가능합니다.
        </p>
      </div>
    </div>
  );
};

// ===================== 목록 페이지 =====================
export default function EducationApplyPage({ onLogout }) {
  const [page,        setPage]        = useState("list");
  const [list,        setList]        = useState([]);
  const [selected,    setSelected]    = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [toast,       setToast]       = useState("");
  const [yearFilter,  setYearFilter]  = useState(new Date().getFullYear().toString());
  const [yearList, setYearList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  const user    = JSON.parse(localStorage.getItem("user") || "{}");
  const empNo   = user.emp_no || "";
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 2500); };

  const loadList = async (year) => {
    setLoading(true);
    try {
      const res = await fetchEducationList(year);
      if (res.success) setList(res.data);
      else if (res.message === '토큰이 만료되었습니다. 다시 로그인해주세요.') onLogout();
    } catch (err) {
      showToast("❌ 목록 조회 실패");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadList(yearFilter); }, [yearFilter]);
  useEffect(() => {
    const codes = getCodeList('YEAR_LIST');
    setYearList(codes);
    if (codes.length > 0 && !codes.find(c => c.code === new Date().getFullYear().toString())) {
      setYearFilter(codes[0].code);
    }
  }, []);

  const paginated  = list.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const totalPages = Math.ceil(list.length / PAGE_SIZE);

  if (page === "detail" && selected) {
    return <DetailPage item={selected} onBack={() => setPage("list")} empNo={empNo} />;
  }

  return (
    <div style={styles.wrap}>
      <Toast msg={toast} />

      {/* 헤더 */}
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>📝 교육 신청</h1>
        <span style={styles.statBadge}>총 {list.length}건</span>
      </div>

      {/* 연도 필터 */}
      <div style={styles.filterCard}>
        <select style={styles.select} value={yearFilter}
          onChange={e => { setYearFilter(e.target.value); setCurrentPage(1); }}>
          {yearList.map(y => (
            <option key={y.code} value={y.code}>{y.code_nm}</option>
          ))}
        </select>
      </div>

      {/* 목록 */}
      <div style={styles.card}>
        {loading ? (
          <div style={styles.empty}>⏳ 불러오는 중...</div>
        ) : paginated.length === 0 ? (
          <div style={styles.empty}>등록된 교육이 없습니다.</div>
        ) : paginated.map((item, i) => (
          <div key={i} style={styles.listItem}
            onClick={() => { setSelected(item); setPage("detail"); }}
            onMouseEnter={e => e.currentTarget.style.background = "#f8faff"}
            onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
            <div style={styles.listLeft}>
              <Badge color="blue">{item.edu_year}년 {item.seq_no}차</Badge>
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <span style={styles.eduName}>{item.edu_name}</span>
                <span style={styles.dateRange}>📅 {item.start_dt} ~ {item.end_dt}</span>
              </div>
            </div>
            <span style={styles.arrow}>›</span>
          </div>
        ))}
        {totalPages > 1 && (
          <div style={styles.pagination}>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <button key={p}
                style={{ ...styles.pageBtn, ...(p === currentPage ? styles.pageBtnActive : {}) }}
                onClick={() => setCurrentPage(p)}>{p}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  wrap        : { background: "#f5f7fa", padding: 16, fontFamily: "'Noto Sans KR', sans-serif" },
  pageHeader  : { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  pageTitle   : { fontSize: 20, fontWeight: 800, color: "#1a1a2e", margin: 0 },
  statBadge   : { background: "#e8f4fd", color: "#1677ff", border: "1px solid #bae0ff", borderRadius: 8, padding: "5px 12px", fontSize: 12, fontWeight: 600 },
  filterCard  : { background: "#fff", borderRadius: 12, padding: "12px 14px", marginBottom: 12, display: "flex", gap: 10, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" },
  select      : { border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "8px 12px", fontSize: 14, outline: "none", background: "#fff", cursor: "pointer" },
  card        : { background: "#fff", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.06)", overflow: "hidden", padding: 16 },
  listItem    : { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 4px", borderBottom: "1px solid #f5f5f5", cursor: "pointer", transition: "background 0.15s" },
  listLeft    : { display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 },
  eduName     : { fontSize: 14, fontWeight: 600, color: "#1a1a2e", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  dateRange   : { fontSize: 12, color: "#aaa" },
  arrow       : { fontSize: 20, color: "#ccc", fontWeight: 300, flexShrink: 0 },
  empty       : { textAlign: "center", padding: "50px 0", color: "#bbb", fontSize: 14 },
  pagination  : { display: "flex", justifyContent: "center", gap: 6, padding: "14px 0" },
  pageBtn     : { width: 30, height: 30, borderRadius: 6, border: "1px solid #e0e0e0", background: "#fff", cursor: "pointer", fontSize: 13, color: "#666" },
  pageBtnActive: { background: "#1677ff", color: "#fff", border: "1px solid #1677ff" },
  backBtn     : { background: "none", border: "1px solid #e0e0e0", borderRadius: 8, padding: "8px 14px", fontSize: 13, color: "#666", cursor: "pointer", marginBottom: 12, display: "block" },
  infoRow     : { display: "flex", gap: 10, alignItems: "center" },
  infoLabel   : { fontSize: 18, color: "#aaa", fontWeight: 600, minWidth: 90 },
  infoValue   : { fontSize: 21, color: "#1a1a2e", fontWeight: 500 },
}